import type {
  InteractiveSurveyLoginRequest,
  InteractiveSurveyLoginResponse,
} from "@/features/auth/types";
import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";

/**
 * Interactive Survey Authentication API
 * Handles 4-digit passcode authentication for survey access
 */
export const interactiveSurveyAuth = createApi({
  reducerPath: "interactiveSurveyAuthApi",
  baseQuery: fetchBaseQuery({
    baseUrl: `${import.meta.env.VITE_SURVEY_SERVICE_BASE_URL}interactive`,
    prepareHeaders: (headers) => {
      headers.set("Content-Type", "application/json");

      // Include interactiveToken if available
      const interactiveToken = getInteractiveToken();
      headers.set("sec-fetch-site", "same-origin");
      headers.set("interactiveToken", interactiveToken || "");

      // Include surveyId if available
      const surveyId = getSurveyId();
      if (surveyId) {
        headers.set("X-Survey-ID", surveyId);
      }

      return headers;
    },
  }),
  endpoints: (builder) => ({
    loginWithPasscode: builder.mutation<
      InteractiveSurveyLoginResponse,
      InteractiveSurveyLoginRequest
    >({
      query: (credentials) => ({
        url: "login",
        method: "POST",
        body: credentials,
      }),
    }),
  }),
});

export const { useLoginWithPasscodeMutation } = interactiveSurveyAuth;

/**
 * Token management for interactive survey authentication
 */
export const saveInteractiveToken = (token: string, surveyId: string) => {
  localStorage.setItem("interactiveToken", token);
  localStorage.setItem("surveyId", surveyId);
};

export const getInteractiveToken = (): string | null => {
  return localStorage.getItem("interactiveToken");
};

export const getSurveyId = (): string | null => {
  return localStorage.getItem("surveyId");
};

export const clearInteractiveAuth = () => {
  localStorage.removeItem("interactiveToken");
  localStorage.removeItem("surveyId");
};
